import { useState, useEffect, useCallback, } from 'react';
import { connect } from 'react-redux';

import { siteboardAction } from '../actions/defaultActions';
import { 
  Email, GET_PROJECT_SCHEDULE_CONTACTS, SET_PROJECT_CONTACTS_DIALOGUE_ID, SET_ALERT, ADD_PROJECT_SCHEDULE_CONTACTS, GET_CONTACT_SHARED_SCHEDULE_STATS,
} from '../actions/types';

import PopupWindow from './PopupWindow';
import ProjectContacts from './ProjectContacts';
import PushButton from './PushButton';

// icon imports
import AddIcon from '../icons/addIcon';
import SearchIcon from '../icons/searchIcon';
import XIcon from '../icons/xIcon';

const ProjectContactsDialogue = ({ _projects, _projectContactsDialogueId, siteboardAction }) => {

  const CONTACT_LIST_SCREEN = 'CONTACT_LIST_SCREEN';
  const ADD_EMAILS_SCREEN = 'ADD_EMAILS_SCREEN';

  const [emailsInput, setEmailsInput] = useState('');
  const [currentScreen, setCurrentScreen] = useState(CONTACT_LIST_SCREEN);
  const [hasChangedScreen, setHasChangedScreen] = useState(false);
  const [inputListFilter, setInputListFilter] = useState('');

  const selectedProject = _projectContactsDialogueId in _projects ? _projects[_projectContactsDialogueId] : null;

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_PROJECT_CONTACTS_DIALOGUE_ID, { projectId: '' });
  }, [siteboardAction]);

  useEffect(() => {
    if(currentScreen === CONTACT_LIST_SCREEN) return;
    setHasChangedScreen(true);
  }, [currentScreen]);

  // Fetch schedule contacts from the server. If we already have them this will do nothing
  useEffect(() => {
    if(selectedProject === null) return;
    siteboardAction(GET_PROJECT_SCHEDULE_CONTACTS, {projectId: selectedProject.id});
    //siteboardAction(GET_CONTACT_SHARED_SCHEDULE_STATS, { projectId: projectId, scheduleId: effectiveScheduleId, });
  }, [siteboardAction, selectedProject]);

  if(selectedProject === null) return (<></>);

  const footer = () => {
    if(currentScreen !== CONTACT_LIST_SCREEN) return <></>;
    return (
      <div className='flex flex-col gap-2 justify-center text-white p-4 pt-2'>
        <PushButton 
          textColor='black'
          bgColor='#f9a928'
          hoverBgColor='#f79800'
          onClick={() => {
            setCurrentScreen(ADD_EMAILS_SCREEN);
          }}
        >
          <div className='flex items-center gap-1' ><AddIcon color='#000' /> Add Emails</div>
        </PushButton>
      </div>
    );
  };

  return (
    <PopupWindow 
      title={currentScreen === CONTACT_LIST_SCREEN ? 'Distribution List' : 'Add Emails'}
      closeDialogue={closeDialogue} 
      bgStyle='bg-[#333333]'
      footer={footer}
    >
      <div className={`flex flex-col gap-4 max-w-screen-lg text-white ${currentScreen === CONTACT_LIST_SCREEN ? '' : 'hidden'} ${hasChangedScreen ? 'animate-right-fade' : ''}`}>
        <div className='w-[90vw] max-w-full' />

        <div className='flex flex-col justify-between'>
          <div><b>{selectedProject.contactCount}</b> contacts for <b>{selectedProject.name}</b>.</div>
          <div className='opacity-70'>Contacts receive published schedules via email.</div>
        </div>
        {/* filter contacts */}
        <div className="relative w-full">
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon size='18' color='#aaa' />
          </div>
          <input
            type="text"
            value={inputListFilter}
            onChange={(event) => setInputListFilter(event.target.value)}
            placeholder='Search contacts...'
            className="
              w-full rounded-md
              py-2.5 pl-10 pr-10
              text-sm text-white 
              placeholder:text-gray-400
              transition-colors
              bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]
            "
          />
          {inputListFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setInputListFilter('')}
              aria-label="Clear search"
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                text-gray-400 transition-colors
                hover:text-gray-700
              "
            >
              <XIcon size='18' />
            </button>
          )}
        </div>
        {selectedProject.contactCount > 0 && 
          <div className='bg-neutral-700 py-2 rounded-b-lg rounded-t-lg'>
            <ProjectContacts projectId={selectedProject.id} subscriptionId={selectedProject.subscriptionId} listFilter={inputListFilter} />
          </div>
        }
      </div>
      <div className={`max-w-screen-sm bg-[#333] text-white animate-left-fade ${currentScreen === ADD_EMAILS_SCREEN ? '' : 'hidden'}`}>
        <div className='flex flex-col gap-y-4 w-[90vw] max-w-full'>
          <div>
            <div className=''>Enter recipient emails below.</div>
            <div className=''>Separate multiple emails with a comma or new line.</div>
          </div>
          <textarea 
            className={'w-full text-white leading-[1.4em] rounded-md px-3 py-2 bg-[#444] hover:bg-[#555] focus:bg-[#555] resize-none'}
            value={emailsInput}
            type="text"
            placeholder='Paste or type emails here'
            rows={12}
            onChange={(event) => {
              setEmailsInput(event.target.value);
            }}
          >
          </textarea>
          <div className='flex gap-4'>
            <button  
              className={`border border-[#888] hover:border-[#000] hover:bg-[#000] text-white w-full py-[7px] font-bold select-none rounded-lg`}
              onClick={() => { setCurrentScreen(CONTACT_LIST_SCREEN); }}
            >
              Cancel
            </button>
            <PushButton 
              textColor='black'
              bgColor='#f9a928'
              hoverBgColor='#f79800'
              onClick={() => {
                const emails = emailsInput.split(/[\n,]+/).map(email => email.trim()).filter(email => email.length > 0);
                for(const email of emails) {
                  if(!Email.isValid(email)) {
                    siteboardAction(SET_ALERT, { message: `${email} is not a valid email. Please check that you entered it correctly, or remove it.` }); 
                    return;
                  }
                }
                if(emails.length === 0) {
                  siteboardAction(SET_ALERT, { message: 'Enter at least one email' }); 
                  return;
                }
                const onSuccess = (validOutput) => {
                  setEmailsInput('');
                  setCurrentScreen(CONTACT_LIST_SCREEN);
                };
                siteboardAction(ADD_PROJECT_SCHEDULE_CONTACTS, {
                  projectId: selectedProject.id,
                  emails: emails,
                }, onSuccess);
              }}
            >
              <div className='flex items-center gap-1' ><AddIcon color='#000' /> Add Emails to List</div>
            </PushButton>
          </div>
        </div>
      </div>
    </PopupWindow>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
  _projectContactsDialogueId: state.localEphemeral.projectContactsDialogueId,
}};
export default connect(mapStateToProps, { siteboardAction })(ProjectContactsDialogue);
